// src/components/StrategyPanel.js
import React from 'react';
import '../styles/StrategyPanel.css';

/**
 * data.visualization:
 * {
 *   executiveSummary?: string,
 *   actions?: [{ title, description?, roiScore?, impactScore?, timelineQuarters?, riskIfIgnored?, priorityLabel?, priorityScore? }],
 *   risks?: [{ text, severity?, likelihood? }],
 *   matrices?: { timeline?: [{ name, quarters }] }
 * }
 */

const IS_MOBILE = typeof window !== 'undefined'
  && window.matchMedia
  && window.matchMedia('(max-width: 480px)').matches;

const clamp = (s = '', n = 140) => {
  const t = String(s || '').trim();
  if (IS_MOBILE) return t;            // show full text on phones
  return t.length <= n ? t : t.slice(0, n - 1).trimEnd() + '…';
};

const score = a => {
  const r = Number(a?.roiScore || 0);
  const i = Number(a?.impactScore || 0);
  const q = Math.max(0.5, Number(a?.timelineQuarters || 2));
  return (r + i) / q;
};
const sortByPriority = arr => [...arr].sort((a, b) => (b?.priorityScore ?? score(b)) - (a?.priorityScore ?? score(a)));

export default function StrategyPanel({ data = {}, loading, error }) {
  const viz    = data?.visualization || {};
  const acts   = Array.isArray(viz.actions) ? sortByPriority(viz.actions) : [];
  const risks  = Array.isArray(viz.risks) ? viz.risks : [];
  const timeline = (viz.matrices?.timeline || acts.map(a => ({ name: a.title, quarters: a.timelineQuarters || 2 }))).slice(0, 12);

  if (loading) {
    return (
      <div className="sp-root rows">
        <div className="card sp-skel">
          <div className="row-inner">
            <div className="shimmer h-7 w-80" />
            <div className="shimmer h-4 w-96" />
            <div className="shimmer h-28 mt-2" />
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="sp-root rows">
        <div className="card sp-error">
          <div className="row-inner">
            <div className="err-title">Couldn’t generate strategy</div>
            <div className="err-body">{String(error)}</div>
          </div>
        </div>
      </div>
    );
  }

  // Split: top recommendations first, remainder shown as opportunities
  const recos = acts.slice(0, 3);
  const opps  = acts.slice(3);

  return (
    <div className="sp-root rows">
      {/* Row 1: Strategic Plan bullets */}
      <section className="card row plan-strip">
        <div className="row-inner">
          <div className="row-title">Strategic Plan — Next 90 Days</div>
          <ul className="plan-inline">
            {(recos.length ? recos : acts.slice(0, 3)).map((a, i) => (
              <li key={`pl-${i}`} className="plan-chip">
                <div className={`pill ${a?.priorityLabel ? a.priorityLabel.toLowerCase() : 'na'}`}>{a?.priorityLabel || '—'}</div>
                <div className="chip-text">
                  <span className="chip-title">{a?.title || 'Untitled'}</span>
                  {a?.description ? <span className="chip-desc"> — {clamp(a.description, 110)}</span> : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Row 2: Recommendations — full width responsive cards */}
      <section className="card row recos-strip">
        <div className="row-inner">
          <div className="row-title">Recommendations</div>
          <div className="cards-fluid">
            {(recos.length ? recos : acts.slice(0, 3)).map((a, i) => (
              <div className="card-fluid" key={`r-${i}`}>
                <div className="top">
                  <div className="title">{a?.title || 'Untitled'}</div>
                  <div className="badges">
                    {a?.roiScore ? <span className="badge">ROI {a.roiScore}</span> : null}
                    {a?.impactScore ? <span className="badge">Impact {a.impactScore}</span> : null}
                    {a?.timelineQuarters ? <span className="badge muted">{a.timelineQuarters} qtrs</span> : null}
                  </div>
                </div>
                {a?.description ? <p className="desc">{clamp(a.description, 220)}</p> : null}
                {a?.riskIfIgnored ? <p className="foot"><span className="label">If ignored:</span> {clamp(a.riskIfIgnored, 140)}</p> : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Row 3: Opportunities — full width responsive cards */}
      <section className="card row opps-strip">
        <div className="row-inner">
          <div className="row-title success">Opportunities</div>
          <div className="cards-fluid small">
            {(opps.length ? opps : acts).slice(0, 12).map((a, i) => (
              <div className="card-fluid" key={`o-${i}`}>
                <div className="top">
                  <div className="title">{a?.title || 'Untitled'}</div>
                  <div className="badges">
                    {a?.roiScore ? <span className="badge">ROI {a.roiScore}</span> : null}
                    {a?.impactScore ? <span className="badge">Impact {a.impactScore}</span> : null}
                  </div>
                </div>
                {a?.description ? <p className="desc">{clamp(a.description, 160)}</p> : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Row 4: Risks — full width responsive chips */}
      <section className="card row risks-strip">
        <div className="row-inner">
          <div className="row-title danger">Risk Warnings</div>
          <ul className="risk-fluid">
            {(risks.length ? risks : [{ text: 'No risks identified.' }]).slice(0, 18).map((r, i) => (
              <li className="risk-chip" key={`k-${i}`}>
                <div className="risk-main">{r?.text || '—'}</div>
                <div className="risk-tags">
                  {r?.severity ? <span className="badge danger">{r.severity}</span> : null}
                  {r?.likelihood ? <span className="badge muted">{r.likelihood} likelihood</span> : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Row 5: Timeline — full width chips */}
      <section className="card row timeline-strip">
        <div className="row-inner">
          <div className="row-title">Executive Timeline</div>
          <div className="timeline-fluid">
            {timeline.map((t, i) => (
              <div className="tl-chip" key={`t-${i}`}>
                <div className="tl-name">{clamp(t.name, 28)}</div>
                <div
                  className="tl-bar"
                  style={{ width: `${Math.min(100, Math.max(26, (Math.min(4, Math.max(1, t.quarters || 1)) * 26))) }%` }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
